import {
  TechRadarApi,
  techRadarApiRef,
} from '@backstage-community/plugin-tech-radar';
import type { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/**
 * Multi-radar client for the Tech Radar plugin.
 *
 * Uses discoveryApiRef.getBaseUrl('tech-radar') to resolve the absolute backend
 * URL — the same pattern all other Backstage plugins use. This means:
 * - Dev:  http://localhost:7007/api/tech-radar/<id>  (bypasses the Vite proxy)
 * - Prod: https://<backstage-host>/api/tech-radar/<id>
 *
 * The plugin ID ('tech-radar') must match the pluginId in techRadarPlugin.ts.
 */
export class TechRadarClient implements TechRadarApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async load(id: string | undefined): Promise<TechRadarLoaderResponse> {
    const radarId = id ?? 'enterprise';

    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar');
    const url = `${baseUrl}/${radarId}`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw new Error(
        `Failed to load tech radar data for '${radarId}': ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    // Timeline dates come back as ISO strings — convert to Date objects.
    return {
      ...data,
      entries: data.entries.map((entry: any) => ({
        ...entry,
        timeline: entry.timeline.map((point: any) => ({
          ...point,
          date: new Date(point.date),
        })),
      })),
    } as TechRadarLoaderResponse;
  }
}

export { techRadarApiRef };

